/**
 * Tooling.tsx — the Manager Metrics Dashboard "adoption & reuse" page (MD-F17..F28).
 *
 * TRIMMED TO CHARTS (this pass). Russell asked for "WAY simpler, just charts", so the page now ends at
 * the most-adopted chart: the three adoption/catalogue tiles at the top, then the green bar chart of the
 * most-adopted tools and its table. The sections that used to follow — creators & consumers, the
 * time-saved estimate, tools needing attention and review-queue health — were REMOVED FROM THE VIEW, not
 * from the API: the /tooling route still computes every one of them (reversible via git), the page simply
 * stops rendering them. Adoption is the ONLY engagement signal here — no like/save/rating/badge/follower
 * appears in any form (MD-NFR10).
 *
 * TEAM SCOPE (0049): a manager viewing their own team sees the two adoption COUNTS narrow to the team's
 * roster (how much did MY people take up). The most-adopted list stays platform-wide — it ranks tools,
 * not the team, and carries no team meaning. See getAdoptionSummaryWith for exactly which figures move.
 */
import { Link, useLoaderData } from 'react-router';
import type { PageState, ToolingResponse } from './api';
import { SignedOut } from '../admin/shared';
import { NotPermitted } from '../admin/overlay';
import { AsOfNote, MetricCard, periodLabel } from './shared';
import { BarChart } from './charts';

export function Tooling() {
  const data = useLoaderData() as PageState<ToolingResponse>;
  if (data.state === 'signedOut')
    return <SignedOut title="Management dashboard" returnTo="/dashboard/tooling" />;
  if (data.state === 'forbidden') return <NotPermitted what="Management dashboard" />;

  const { adoption, growth, filter } = data.data;
  const window = periodLabel(filter.windowDays);
  const { asOf } = data.data;

  return (
    <div>
      <h2>Adoption &amp; reuse</h2>
      {/* MD-F24: one line for the page's period. "Adoptions in period" and the newly-published count move
          with the selector; "adoptions all time" and the most-adopted table are all-time and say so in
          their own copy — so the banner keeps its "except where a figure states its own period" caveat. */}
      <p className="tb-filter-live" aria-live="polite">
        Showing the {window}
        {filter.teamId != null
          ? ', scoped to your team'
          : filter.departmentId != null
            ? ', filtered by department'
            : ', all departments'}
        , except where a figure states its own period.
      </p>
      <AsOfNote asOf={asOf} />

      <div className="tb-dash-grid">
        {/* Both counts are tool-person adoptions, NOT people: the engine counts each person once per
            tool they cloned, so a colleague who adopted three tools contributes three. The old copy
            called them "distinct adopters", which invited a manager to read a people-count off a
            figure that is a third larger than one — and a people-count would be subtractable from the
            department breakdown on the Overview. Under a team scope these two narrow to the team's
            roster; the most-adopted list below stays platform-wide (it ranks tools, not the team). */}
        <MetricCard
          label="Adoptions in period"
          figure={adoption.adoptionsInWindow.toLocaleString()}
          note={`Tools taken up, ${window}, across ${adoption.distinctAdoptedTools.toLocaleString()} ${adoption.distinctAdoptedTools === 1 ? 'tool' : 'tools'}. Each person counts once per tool they adopted.`}
        />
        <MetricCard
          label="Adoptions all time"
          figure={adoption.adoptionsAllTime.toLocaleString()}
          note="The whole life of the catalogue, on the same basis."
        />
        <MetricCard
          label="Approved tools"
          figure={growth.approvedTotal.toLocaleString()}
          note={`${growth.newlyPublishedInWindow.toLocaleString()} newly published in the ${window}.`}
        />
      </div>

      <h3>Most-adopted tools</h3>
      {adoption.mostAdopted.length === 0 ? (
        <p className="tb-empty">No tool has a qualifying adopter yet.</p>
      ) : (
        <>
          {/* A chart of TOOLS (which may be ranked) and not people (which never are) — the series is
              the same all-time distinct-adopter counts the table shows. The table's own caption sits
              directly beneath and states the "whole life of the catalogue, not the {window}" horizon
              for both, so the chart adds none of its own. No value here is ever suppressed: a tool
              only appears in mostAdopted once it clears the adopter floor, so every row is real. */}
          <BarChart
            unit="distinct adopters"
            data={adoption.mostAdopted.map((t) => ({
              label: t.name,
              value: t.adoptionCount,
              href: `/tools/${t.toolId}`,
            }))}
          />
          <table className="tb-dash-table">
            {/* ALL TIME, not the selected period — the engine's list query carries no window clause, only
              the window COUNT above does. A ranking of tools sitting under a period banner with no such
              caption invites "these are the month's winners", which is a different table entirely. */}
            <caption className="tb-muted" style={{ captionSide: 'bottom', textAlign: 'left' }}>
              Distinct adopters over the whole life of the catalogue, not the {window}. Tools may be
              ranked here; people never are.
            </caption>
            <thead>
              <tr>
                <th scope="col">Tool</th>
                <th scope="col" className="tb-num">
                  Distinct adopters
                </th>
              </tr>
            </thead>
            <tbody>
              {adoption.mostAdopted.map((t) => (
                <tr key={t.toolId}>
                  <td>
                    <Link to={`/tools/${t.toolId}`}>{t.name}</Link>
                  </td>
                  <td className="tb-num">{t.adoptionCount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
